import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { reorderOperation } from 'store/utils-store';
import {
  depthInequality,
  drillingUnit,
  wellConfiguration,
  linkFields,
  TemplateEvents,
  settingEntity,
} from 'src/enums/settings';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { estimateTypes, distributionTypes } from 'src/enums/tasks';
import { CostType, QuantityType } from 'src/enums/cost-setup';
import { toast } from '@oliasoft-open-source/react-ui-library';
import { nextName } from 'client/common/utils/next-name';
import { apiCallBegan } from 'store/middleware/api/api';
import { typeQuantityMap } from 'src/enums/price-book';
import { ITemplate, IForecastActivityModel } from 'src/common/interfaces/templates.interfaces';
import { ICostCategory, ICostItem, ICostSetup } from 'src/common/interfaces/cost-setup';
import { IPriceBookItem } from 'src/common/interfaces/price-book.interfaces';
import { IActivity, IOperation } from 'src/common/interfaces/sections-data-structure.interfaces';
import { IActivity as IRigOperationTemplateActivity } from 'src/common/interfaces/rig-operations-template.interfaces';
import { updateAllSubCategories } from '../cost-setup/helper/update-all-sub-categories';
import {
  updateActivityIds,
  updateCostSetupIds,
  findConnectedCategories,
  getOperationIdsMap,
} from '../user-settings/user-settings.utils';

enum templateEntityMap {
  user = 'userTemplates',
  company = 'companyTemplates',
}

interface IExtendedPriceBook extends IPriceBookItem {
  priceBookName: string;
}

export const wellConfigurations = [
  {
    label: translations.settings_offshore,
    value: wellConfiguration.OFFSHORE,
  },
  {
    label: translations.settings_onshore,
    value: wellConfiguration.ONSHORE,
  },
];

export const initialTemplate = {
  templateId: '',
  highLevel: {
    name: '',
    wellConfiguration: wellConfiguration.OFFSHORE,
    drillingUnit: drillingUnit.SEMI_SUBMERSIBLE,
    waterDepthInequality: depthInequality.LESS_OR_EQUAL,
    waterDepth: 0,
    wellheadDepthInequality: depthInequality.LESS_OR_EQUAL,
    wellheadDepth: 0,
    performDst: false,
    runCompletion: false,
    performPa: false,
    event: TemplateEvents.DrillingAndCompletion,
  },
  activityModel: [],
  costSetup: {
    costItem: [],
    costCategory: [],
  },
};

export const initialOperation = {
  id: '',
  name: '',
  from: 0,
  depthFromLink: linkFields.CUSTOM,
  depthToLink: linkFields.CUSTOM,
  to: 0,
  comments: '',
  activities: [],
  operationCode: null,
  subOperationCode: null,
  sectionsOperationId: null,
};

export const initialActivity = {
  parentId: 0,
  name: '',
  certainty: 100,
  estimateType: estimateTypes.OPERATION_TIME,
  from: 0,
  to: 0,
  min: 1,
  most: 2,
  max: 3,
  activityCode: null,
  comments: '',
  distribution: distributionTypes.PERT,
  withBranch: false,
  isBranch: false,
  isApplyFromOffset: false,
  rigTemplateActivityId: null,
  isLinked: false,
  isImported: false,
};

export const createInitialBranch = (parentId: string) => ({
  parentId,
  id: uuidv4(),
  name: 'New Branch',
  certainty: 100,
  estimateType: estimateTypes.OPERATION_TIME,
  from: 0,
  to: 0,
  min: 0,
  most: 0,
  max: 0,
  activityCode: null,
  comments: '',
  distribution: distributionTypes.PERT,
  withBranch: false,
  isBranch: true,
});

export const initialCostItem = {
  cost: 0,
  name: '',
  type: CostType.DayRate as string,
  currency: 'USD',
  costItemId: null,
  parentId: '',
  operations: [],
  quantity: QuantityType.Length as string,
  length: 0,
  volume: 0,
  unitCost: 0,
  itemCount: 0,
  holeLength: 0,
  extraTimeValue: 0,
  extraTimeUnit: 'days',
  isStandalone: false,
  isDistributed: false,
  costCodesId: null,
  vendor: null,
  notes: '',
  priceBookItemId: null,
  isLinked: false,
};

type TemplatesState = {
  isFetching: boolean;
  isAdding: boolean;
  showTemplatesForm: boolean;
  showOperationsForm: boolean;
  showActivityForm: boolean;
  showDeleteOperationsModal: boolean;
  showDeleteActivitiesModal: boolean;
  showDeleteCostItemModal: boolean;
  showImportOperationModal: boolean;
  showDeleteCostCategoryModal: boolean;
  selectedId: string | null;
  sectionTypes: any[];
  codes: any[];
  userTemplates: ITemplate[];
  companyTemplates: ITemplate[];
  error: any;
};

const templates = createSlice({
  name: 'templates',
  initialState: {
    isFetching: false,
    isAdding: false,
    showTemplatesForm: false,
    showOperationsForm: false,
    showActivityForm: false,
    showDeleteOperationsModal: false,
    showDeleteActivitiesModal: false,
    showDeleteCostItemModal: false,
    showImportOperationModal: false,
    showDeleteCostCategoryModal: false,
    selectedId: null,
    sectionTypes: [],
    codes: [],
    userTemplates: [],
    companyTemplates: [],
    error: null,
  } as TemplatesState,
  reducers: {
    userTemplatesRequested: (templates) => {
      templates.isFetching = true;
      templates.userTemplates = [];
    },
    companyTemplatesRequested: (templates) => {
      templates.isFetching = true;
      templates.companyTemplates = [];
    },
    userTemplatesReceived: (templates, action) => {
      templates.isFetching = false;
      templates.userTemplates = action.payload;
    },
    companyTemplatesReceived: (templates, action) => {
      templates.isFetching = false;
      templates.companyTemplates = action.payload;
    },
    templatesRequestFailed: (templates) => {
      templates.isFetching = false;
    },

    saveTemplatesRequested: (templates) => {
      templates.isAdding = true;
    },

    templatesSaved: (templates, action) => {
      const { response, entity }: { response: any; entity: keyof typeof templateEntityMap } = action.payload;
      templates.isAdding = false;
      templates[templateEntityMap[entity]] = response;
      templates.showTemplatesForm = false;
      templates[templateEntityMap[entity]] = templates[templateEntityMap[entity]].map((item, i) => {
        item.active = i === templates[templateEntityMap[entity]].length - 1;
        return item;
      });
    },

    templatesSaveFailed: (templates) => {
      templates.isAdding = false;
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.settings_errorAddingTemplate)}`,
        },
      });
    },
    templateSelected: (templates, action) => {
      const { templateId, entity }: { templateId: string; entity: keyof typeof templateEntityMap } = action.payload;
      templates[templateEntityMap[entity]] = templates?.[templateEntityMap[entity]]?.map((item: any) => ({
        ...item,
        active: item.templateId === templateId,
      }));
      templates.showTemplatesForm = true;
    },
    templateUpdated: (templates, action) => {
      const {
        index,
        highLevelData,
        entity,
      }: { index: number; highLevelData: any; entity: keyof typeof templateEntityMap } = action.payload;
      templates[templateEntityMap[entity]][index].highLevel = highLevelData;
    },
    showTemplatesFormUpdated: (templates, action) => {
      templates.showTemplatesForm = action.payload;
    },
    sectionTypesFetched: (templates, action) => {
      templates.isFetching = false;
      templates.sectionTypes = action.payload.linkedSections;
    },
    sectionTypeSelected: (templates, action) => {
      const { sectionId, entity }: { sectionId: string; entity: keyof typeof templateEntityMap } = action.payload;
      templates.sectionTypes = templates.sectionTypes.map((item) => {
        item.active = item.linkedSectionId === sectionId;
        return item;
      });
      const templatesData = templates[templateEntityMap[entity]];
      const activeTemplateIndex = templatesData.findIndex((item) => item.active);
      templates[templateEntityMap[entity]][activeTemplateIndex].activityModel = templatesData[
        activeTemplateIndex
      ].activityModel.map((item) => {
        item.active = false;
        return item;
      });
    },
    showOperationsFormUpdated: (templates, action) => {
      templates.showOperationsForm = action.payload;
    },
    showDeleteOperationsModalUpdated: (templates, action) => {
      templates.showDeleteOperationsModal = action.payload.visible;
      templates.selectedId = action.payload.selectedId;
    },
    operationAdded: (templates, action) => {
      const {
        templateIndex,
        operation,
        entity,
      }: { templateIndex: number; operation: any; entity: keyof typeof templateEntityMap } = action.payload;
      templates[templateEntityMap[entity]][templateIndex].activityModel.push(operation);
      templates[templateEntityMap[entity]][templateIndex].activityModel.map(
        (item) => (item.active = item.id === operation.id),
      );
      templates.isAdding = false;
      templates.showOperationsForm = true;
    },

    operationUpdated: (
      templates,
      action: {
        payload: { updatedTemplates: ITemplate[]; entity: keyof typeof templateEntityMap };
      },
    ) => {
      const { updatedTemplates, entity } = action.payload;
      (templates[templateEntityMap[entity] as keyof TemplatesState] as ITemplate[]) = updatedTemplates;
      templates.isAdding = false;
      templates.showOperationsForm = false;
    },
    operationSelected: (templates, action) => {
      const { operationId, entity }: { operationId: string; entity: keyof typeof templateEntityMap } = action.payload;
      const activeTemplateIndex = templates[templateEntityMap[entity]].findIndex((item: any) => item.active);
      templates[templateEntityMap[entity]][activeTemplateIndex].activityModel = templates[templateEntityMap[entity]][
        activeTemplateIndex
      ].activityModel.map((item: any) => {
        item.active = item.id === operationId;
        return item;
      });
    },
    operationRemoved: (templates, action) => {
      const {
        templateIndex,
        operationId,
        entity,
      }: { templateIndex: number; operationId: string; entity: keyof typeof templateEntityMap } = action.payload;
      const { activityModel } = templates[templateEntityMap[entity]][templateIndex];
      templates[templateEntityMap[entity]][templateIndex].activityModel = activityModel?.filter(
        (operation) => operation.id !== operationId,
      );
      templates.isAdding = false;
      templates.showDeleteOperationsModal = false;
    },
    codesRequested: (templates) => {
      templates.codes = [];
    },
    codesReceived: (templates, action) => {
      templates.codes = action.payload.codes;
    },
    activitySaveRequested: (templates, action) => {
      const {
        templateIndex,
        operationIndex,
        activity,
        entity,
      }: { templateIndex: number; operationIndex: number; activity: any; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]][templateIndex].activityModel[operationIndex].activities.push(activity);
      templates[templateEntityMap[entity]][templateIndex].activityModel[operationIndex].activities = templates[
        templateEntityMap[entity]
      ][templateIndex].activityModel[operationIndex].activities.map((item) => {
        item.active = item.id === activity.id;
        return item;
      });
    },
    activitySaved: (templates) => {
      templates.isAdding = false;
      templates.showActivityForm = true;
    },

    activityRemoved: (templates, action) => {
      const { updatedTemplates, entity }: { updatedTemplates: ITemplate[]; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]] = updatedTemplates;

      templates.isAdding = false;
      templates.showDeleteActivitiesModal = false;
    },
    showDeleteActivitiesModalUpdated: (userSettings, action) => {
      userSettings.showDeleteActivitiesModal = action.payload.visible;
      userSettings.selectedId = action.payload.selectedId;
    },
    activitiesReorderRequested: (templates, action) => {
      const {
        templateIndex,
        operationIndex,
        activities,
        entity,
      }: {
        templateIndex: number;
        operationIndex: number;
        activities: IForecastActivityModel[];
        entity: keyof typeof templateEntityMap;
      } = action.payload;
      templates[templateEntityMap[entity]][templateIndex].activityModel[operationIndex].activities = activities;
    },
    showActivitiesForm: (templates, action) => {
      const { activityId, entity }: { activityId: string; entity: keyof typeof templateEntityMap } = action.payload;
      const entityTemplates = templates[templateEntityMap[entity]];
      const activeTemplateIndex = entityTemplates.findIndex((item) => item.active);
      const activeOperationIndex = entityTemplates[activeTemplateIndex].activityModel.findIndex((item) => item.active);
      templates[templateEntityMap[entity]][activeTemplateIndex].activityModel[activeOperationIndex].activities =
        entityTemplates[activeTemplateIndex].activityModel[activeOperationIndex].activities.map((item) => {
          item.active = item.id === activityId;
          return item;
        });
      templates.showActivityForm = true;
    },
    closeActivitiesForm: (templates) => {
      templates.showActivityForm = false;
    },
    activityUpdateRequested: (templates, action) => {
      const { updatedTemplates, entity }: { updatedTemplates: ITemplate[]; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]] = updatedTemplates;
    },
    activityUpdated: (templates) => {
      templates.isAdding = false;
      templates.showActivityForm = false;
    },
    activitiesSaveRequested: (templates, action) => {
      const {
        templateIndex,
        operationIndex,
        activities,
        entity,
      }: { templateIndex: number; operationIndex: number; activities: any[]; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]][templateIndex].activityModel[operationIndex].activities.push(...activities);
    },
    activitiesSaved: (templates) => {
      templates.isAdding = false;
    },
    showImportOperationModalUpdated: (templates, action) => {
      templates.showImportOperationModal = action.payload;
    },
    operationsImportRequested: (templates, action) => {
      const {
        templateIndex,
        mergedOperations,
        entity,
      }: { templateIndex: number; mergedOperations: IForecastActivityModel[]; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]][templateIndex].activityModel.push(...mergedOperations);
    },
    operationsImportRequestFailed: (templates, action) => {
      templates.isAdding = false;
      templates.error = action.payload;
    },
    operationsImported: (templates) => {
      templates.isAdding = false;
      templates.showImportOperationModal = false;
    },

    operationReorder: (templates, action) => {
      const {
        response,
        activeTemplateIndex,
        entity,
      }: { response: ITemplate[]; activeTemplateIndex: number; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]][activeTemplateIndex].activityModel =
        response[activeTemplateIndex].activityModel;
      templates.showTemplatesForm = true;
      templates.isAdding = false;
    },

    templateReorder: (
      templates,
      action: { payload: { updatedTemplates: ITemplate[]; entity: keyof typeof templateEntityMap } },
    ) => {
      const { updatedTemplates, entity } = action.payload;
      templates[templateEntityMap[entity]] = updatedTemplates;
      templates.showTemplatesForm = false;
      templates.isAdding = false;
    },

    addCostItemRequested: (
      templates,
      action: { payload: { templateIndex: number; costItem: ICostItem; entity: keyof typeof templateEntityMap } },
    ) => {
      const { templateIndex, costItem, entity } = action.payload;
      templates[templateEntityMap[entity]][templateIndex].costSetup.costItem.push(costItem);
      templates.selectedId = costItem.costItemId;
    },
    addCostItemSaved: (templates) => {
      templates.isAdding = false;
    },
    costItemRemoved: (
      templates,
      action: { payload: { templateIndex: number; costItemIndex: number; entity: keyof typeof templateEntityMap } },
    ) => {
      const { costItemIndex, templateIndex, entity } = action.payload;
      const { costItem } = templates[templateEntityMap[entity]][templateIndex].costSetup;
      costItem.splice(costItemIndex, 1);
      templates[templateEntityMap[entity]][templateIndex].costSetup.costItem = costItem;
      templates.isAdding = false;
    },
    showDeleteCostItemModalUpdated: (templates, action) => {
      templates.showDeleteCostItemModal = action.payload.visible;
      templates.selectedId = action.payload.selectedId;
    },
    costItemSelected: (
      templates,
      action: { payload: { costItemId: string; entity: keyof typeof templateEntityMap } },
    ) => {
      const { costItemId, entity } = action.payload;
      const entityTemplates = templates[templateEntityMap[entity]] as ITemplate[];
      const activeTemplateIndex = entityTemplates.findIndex((item) => item.active);
      const activeTemplate = entityTemplates[activeTemplateIndex];
      const found = activeTemplate?.costSetup?.costItem?.find((item) => item.costItemId === costItemId);
      templates.selectedId = found ? found.costItemId : null;
    },
    costItemUpdatedRequested: (templates, action) => {
      const {
        templateIndex,
        costItemIndex,
        data,
        entity,
      }: { templateIndex: number; costItemIndex: number; data: ICostItem; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]][templateIndex].costSetup.costItem[costItemIndex] = data;
    },
    costItemUpdated: (templates) => {
      templates.isAdding = false;
    },
    addCostCategoryRequested: (templates, action) => {
      const {
        templateIndex,
        costCategory,
        entity,
      }: { templateIndex: number; costCategory: ICostCategory; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]][templateIndex].costSetup.costCategory.push(costCategory);
      templates.selectedId = costCategory.costCategoryId;
    },
    costCategorySelected: (templates, action) => {
      const { costCategoryId, entity }: { costCategoryId: string; entity: keyof typeof templateEntityMap } =
        action.payload;
      const activeTemplateIndex = templates[templateEntityMap[entity]].findIndex((item) => item.active);
      const activeTemplate = templates[templateEntityMap[entity]][activeTemplateIndex];
      const found = activeTemplate?.costSetup?.costCategory?.find((item) => item.costCategoryId === costCategoryId);
      templates.selectedId = found ? found.costCategoryId : null;
    },
    costCategoryUpdatedRequested: (templates, action) => {
      const {
        templateIndex,
        updatedTemplates,
        entity,
      }: { templateIndex: number; updatedTemplates: ITemplate[]; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]][templateIndex].costSetup.costCategory =
        updatedTemplates[templateIndex].costSetup.costCategory;
    },
    costCategoryUpdated: (userSettings) => {
      userSettings.isAdding = false;
    },
    showDeleteCostCategoryModalUpdated: (templates, action) => {
      templates.showDeleteCostCategoryModal = action.payload.visible;
      templates.selectedId = action.payload.selectedId;
    },
    costCategoryRemoved: (templates, action) => {
      const {
        templateIndex,
        filteredCostSetup,
        entity,
      }: { templateIndex: number; filteredCostSetup: ICostSetup; entity: keyof typeof templateEntityMap } =
        action.payload;
      templates[templateEntityMap[entity]][templateIndex].costSetup = filteredCostSetup;
      templates.isAdding = false;
      templates.showDeleteActivitiesModal = false;
    },
    costSetupReorderRequested: (templates, action) => {
      const {
        data,
        templateIndex,
        entity,
      }: { data: ICostSetup; templateIndex: number; entity: keyof typeof templateEntityMap } = action.payload;
      templates[templateEntityMap[entity]][templateIndex].costSetup = data;
    },
    addCostItemFromPriceBookRequested: (templates, action) => {
      const {
        updatedTemplates,
        entity,
      }: {
        updatedTemplates: ITemplate[];
        entity: keyof typeof templateEntityMap;
      } = action.payload;
      templates[templateEntityMap[entity]] = updatedTemplates;
    },
  },
});

export const {
  userTemplatesRequested,
  companyTemplatesRequested,
  userTemplatesReceived,
  companyTemplatesReceived,
  templatesRequestFailed,
  saveTemplatesRequested,
  templatesSaveFailed,
  templateSelected,
  templatesSaved,
  templateUpdated,
  showTemplatesFormUpdated,
  sectionTypeSelected,
  operationSelected,
  operationAdded,
  operationUpdated,
  operationReorder,
  codesRequested,
  codesReceived,
  showOperationsFormUpdated,
  showDeleteOperationsModalUpdated,
  operationRemoved,
  activitySaveRequested,
  activitySaved,
  activityRemoved,
  showDeleteActivitiesModalUpdated,
  activitiesReorderRequested,
  showActivitiesForm,
  closeActivitiesForm,
  activityUpdateRequested,
  activityUpdated,
  activitiesSaveRequested,
  activitiesSaved,
  showImportOperationModalUpdated,
  operationsImportRequested,
  operationsImportRequestFailed,
  operationsImported,
  templateReorder,
  addCostItemRequested,
  addCostItemSaved,
  costItemRemoved,
  showDeleteCostItemModalUpdated,
  costItemSelected,
  costItemUpdatedRequested,
  costItemUpdated,
  addCostCategoryRequested,
  costCategorySelected,
  costCategoryUpdatedRequested,
  costCategoryUpdated,
  showDeleteCostCategoryModalUpdated,
  costCategoryRemoved,
  costSetupReorderRequested,
  sectionTypesFetched,
  addCostItemFromPriceBookRequested,
} = templates.actions;
export default templates.reducer;

export const drillingUnits = [
  {
    label: translations.settings_semiSubmersible,
    value: drillingUnit.SEMI_SUBMERSIBLE,
  },
  {
    label: translations.settings_platformJackup,
    value: drillingUnit.PLATFORM_JACKUP,
  },
  {
    label: translations.settings_tlpHybrid,
    value: drillingUnit.TLP_HYBRID,
  },
  {
    label: translations.settings_landRig,
    value: drillingUnit.LAND_RIG,
  },
];

export const templateEvents = [
  {
    label: translations.settings_drillingAndCompletion,
    value: TemplateEvents.DrillingAndCompletion,
  },
  {
    label: translations.settings_workOver,
    value: TemplateEvents.Workover,
  },
  {
    label: translations.settings_pAndA,
    value: TemplateEvents.PAndA,
  },
];

/**
 * Get settings
 */
export const getUserTemplates = () =>
  apiCallBegan({
    url: '/api/templates/user',
    method: 'GET',
    onStart: userTemplatesRequested.type,
    onSuccess: userTemplatesReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: 'Failed to fetch user templates',
        },
      });
      return { type: templatesRequestFailed.type };
    },
  });

export const getCompanyTemplates = (companyId: string) =>
  apiCallBegan({
    url: `/api/templates/${companyId}`,
    method: 'GET',
    onStart: companyTemplatesRequested.type,
    onSuccess: companyTemplatesReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: 'Failed to fetch company templates',
        },
      });
      return { type: templatesRequestFailed.type };
    },
  });

/**
 * Add initial template
 */
export const addInitialTemplate = (name: string, templates: ITemplate[], entity: string, companyId: string) => {
  const newName = nextName(
    templates.map((template) => template.highLevel.name),
    name,
  );
  const template = {
    templateId: uuidv4(),
    highLevel: { ...initialTemplate.highLevel, name: newName },
    activityModel: initialTemplate.activityModel,
    costSetup: initialTemplate.costSetup,
  };
  const updatedTemplates = [...templates, template];
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: saveTemplatesRequested.type,
    onSuccess: (response) => {
      return {
        type: templatesSaved.type,
        payload: { response, entity },
      };
    },
    onError: templatesSaveFailed.type,
  });
};

/**
 * save template
 */
export const saveTemplates = (templates: ITemplate[], entity: string, companyId: string) => {
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: templates,
    onStart: saveTemplatesRequested.type,
    onSuccess: (response) => {
      return {
        type: templatesSaved.type,
        payload: { response, entity },
      };
    },
    onError: templatesSaveFailed.type,
  });
};

/**
 * Remove template
 */
export const removeTemplate = (templateId: string, templates: ITemplate[], entity: string, companyId: string) => {
  const updatedTemplates = templates.filter((template) => template.templateId !== templateId);
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: saveTemplatesRequested.type,
    onSuccess: (response) => {
      return {
        type: templatesSaved.type,
        payload: { response, entity },
      };
    },
    onError: templatesSaveFailed.type,
  });
};

/**
 * Update template
 */
export const updateTemplate = (
  index: number,
  templates: ITemplate[],
  highLevelData: any,
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  if (
    templates.some(
      (template, i) =>
        template.highLevel.name.toUpperCase().trim() === highLevelData.name.toUpperCase().trim() && i !== index,
    )
  ) {
    toast({
      message: {
        type: 'Error',
        content: `${i18n.t(translations.settings_nameMustBeUnique, {
          type: i18n.t(translations.template),
          name: highLevelData.name,
        })}`,
      },
    });
    highLevelData.name = templates[index].highLevel.name;
  }
  updatedTemplates[index].highLevel = highLevelData;

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onSuccess: () => {
      return {
        type: templateUpdated.type,
        payload: { index, highLevelData, entity },
      };
    },
    onError: templatesSaveFailed.type,
  });
};

export const addInitialOperation = (
  name: string,
  templates: ITemplate[],
  activeSection: any,
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = templates.findIndex((item) => item.active);
  const operation = {
    ...initialOperation,
    id: uuidv4(),
    sectionType: activeSection.sectionId,
    name,
    linkedSectionType: activeSection.linkedSectionId,
  } as unknown as IForecastActivityModel;
  updatedTemplates[templateIndex].activityModel.push(operation);

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onSuccess: () => ({
      type: operationAdded.type,
      payload: { templateIndex, operation, entity },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.userSettings_failedTAddInitialOperation)}`,
        },
      });
      return { type: templatesSaveFailed.type };
    },
  });
};

export const updateOperation = (
  operationData: IForecastActivityModel,
  templates: ITemplate[],
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = templates.findIndex((item) => item.active);
  const operationIndex = templates[templateIndex].activityModel.findIndex((item) => item.active);
  updatedTemplates[templateIndex].activityModel[operationIndex] = operationData;

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onSuccess: () => {
      return {
        type: operationUpdated.type,
        payload: { updatedTemplates, entity },
      };
    },
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.userSettings_failedToUpdateOperation)}`,
        },
      });
      return { type: templatesSaveFailed.type };
    },
  });
};

export const removeOperation = (operationId: string, templates: ITemplate[], entity: string, companyId: string) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = templates.findIndex((item) => item.active);
  const filteredActivityModel = templates[templateIndex].activityModel.filter(
    (operation) => operation.id !== operationId,
  );
  updatedTemplates[templateIndex].activityModel = filteredActivityModel;
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onSuccess: () => ({
      type: operationRemoved.type,
      payload: { operationId, templateIndex, entity },
    }),
    onError: templatesSaveFailed.type,
  });
};

/**
 *  Get active operation codes for operation form
 */
export const getOperationCodes = () =>
  apiCallBegan({
    url: '/api/user-settings/active-codes',
    method: 'GET',
    onStart: codesRequested.type,
    onSuccess: codesReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.userSettings_failedToGetOperationCodes)}`,
        },
      });
    },
  });

export const addInitialActivity = (
  name: string,
  templates: ITemplate[],
  entity: string,
  companyId = '',
  parentId = 0,
  sectionsOperationActivityId = null,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = templates.findIndex((item) => item.active);
  const activity = {
    ...initialActivity,
    id: uuidv4(),
    parentId,
    name,
    sectionsOperationActivityId,
  };
  const operationIndex = templates[templateIndex].activityModel.findIndex((item) => item.active);
  updatedTemplates[templateIndex].activityModel[operationIndex].activities.push(activity);
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: activitySaveRequested.type,
      payload: { templateIndex, operationIndex, activity, entity },
    }),
    onSuccess: () => ({
      type: activitySaved.type,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.userSettings_failedToAddInitialActivity)}`,
        },
      });
    },
  });
};

export const importActivities = (
  templateActivities: IRigOperationTemplateActivity[],
  templates: ITemplate[],
  companyId: string,
  isLinked = false,
  parentId: string | undefined,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const operationIndex = updatedTemplates[templateIndex].activityModel.findIndex((item) => item.active);
  const activities = templateActivities.map((activity) => ({
    ...initialActivity,
    distribution: activity.distribution,
    estimateType: activity.estimateType,
    min: activity.min,
    most: activity.most,
    max: activity.max,
    name: activity.name,
    rigTemplateActivityId: activity.rigOperationTemplateActivityId,
    id: uuidv4(),
    isImported: true,
    isLinked,
    parentId,
    from: linkFields.WELLHEAD_DEPTH,
    to: linkFields.SECTION_TD,
  }));
  updatedTemplates[templateIndex].activityModel[operationIndex].activities.push(...activities);

  const url = `/api/templates/${companyId}`;
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: activitiesSaveRequested.type,
      payload: { templateIndex, operationIndex, activities, entity: 'company' },
    }),
    onSuccess: () => ({
      type: activitiesSaved.type,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.settings_failedToImportActivities)}`,
        },
      });
    },
  });
};

export const removeActivity = (activityId: string, templates: ITemplate[], entity: string, companyId: string) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const operationIndex = updatedTemplates[templateIndex].activityModel.findIndex((item) => item.active);
  const { activities } = updatedTemplates[templateIndex].activityModel[operationIndex];
  const deleteActivityById = (id: string, activities: IForecastActivityModel[]) => {
    const index = activities.findIndex((item) => item.id === id);
    if (index !== -1) {
      activities.splice(index, 1);
      const childItems = activities.filter((item) => item.parentId === id);
      childItems.forEach((childItem) => deleteActivityById(childItem.id, activities));
    }
    return activities;
  };
  const deleteResult = deleteActivityById(activityId, activities);
  updatedTemplates[templateIndex].activityModel[operationIndex].activities = deleteResult;

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onSuccess: () => ({
      type: activityRemoved.type,
      payload: { updatedTemplates, entity },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.userSettings_failedToRemoveActivity)}`,
        },
      });
      return { type: templatesSaveFailed.type };
    },
  });
};

/**
 * Reorder activities
 */
export const reorderActivities = (orderedTasks: any[], templates: ITemplate[], entity: string, companyId: string) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const operationIndex = updatedTemplates[templateIndex].activityModel.findIndex((item) => item.active);
  const orderedTasksMap = orderedTasks.map((task, i) => ({
    id: task.id,
    parentId: task.parent,
    position: i,
  }));

  const activities = updatedTemplates[templateIndex].activityModel[operationIndex].activities.map((item) => {
    const reorderedTask = orderedTasksMap.find((task) => task.id === item.id);

    if (reorderedTask) {
      return {
        ...item,
        parentId: reorderedTask.parentId,
        position: reorderedTask.position,
      };
    }
    return item;
  });
  activities.sort((a, b) => a.position - b.position);
  updatedTemplates[templateIndex].activityModel[operationIndex].activities = activities;

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: activitiesReorderRequested.type,
      payload: { templateIndex, operationIndex, activities, entity },
    }),
  });
};

export const updateActivity = (
  updatedActivity: IForecastActivityModel,
  branches: IForecastActivityModel[],
  templates: ITemplate[],
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const operationIndex = updatedTemplates[templateIndex].activityModel.findIndex((item) => item.active);
  const activityIndex = updatedTemplates[templateIndex].activityModel[operationIndex].activities.findIndex(
    (item) => item.active,
  );
  updatedTemplates[templateIndex].activityModel[operationIndex].activities[activityIndex] = updatedActivity;

  const activities = updatedTemplates[templateIndex].activityModel[operationIndex].activities.filter(
    (activity) => !(activity?.isBranch && activity?.parentId === updatedActivity.id),
  );

  updatedTemplates[templateIndex].activityModel[operationIndex].activities = [
    ...activities,
    ...(updatedActivity?.withBranch ? branches : []),
  ];

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';

  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: activityUpdateRequested.type,
      payload: { updatedTemplates, entity },
    }),
    onSuccess: () => ({
      type: activityUpdated.type,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.userSettings_failedToUpdateActivity)}`,
        },
      });
    },
  });
};

export const importOperations = (
  data: { template: number; operations: string[] },
  templateIndex: number,
  section: { sectionId: string; linkedSectionId: string },
  templates: ITemplate[],
  sectionCodesData: { sectionActivitiesCodes: IActivity[]; sectionOperationsCodes: IOperation[] },
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  const { sectionOperationsCodes, sectionActivitiesCodes } = sectionCodesData;
  const mergedOperations = data.operations.map((id) => {
    const copied = updatedTemplates[data.template].activityModel.find((operation) => operation.id === id);
    const copiedSectionOperationName = sectionOperationsCodes.find(
      (sectionOperation) => sectionOperation.sectionsOperationId === copied?.sectionsOperationId,
    )?.name;
    const findRelatedSectionsOperationId = () => {
      const activeSectionOperations = sectionOperationsCodes.filter(
        (sectionOperation) => sectionOperation.sectionId === section.sectionId,
      );
      const relatedSectionOperationByName = activeSectionOperations.find(
        (sectionOperation) => sectionOperation.name === copiedSectionOperationName,
      );
      if (relatedSectionOperationByName) {
        return relatedSectionOperationByName.sectionsOperationId;
      } else {
        const custumSectionOperationByName: IOperation | undefined = activeSectionOperations.find(
          (sectionOperation) => sectionOperation.name === 'Custom',
        );
        return custumSectionOperationByName?.sectionsOperationId;
      }
    };
    const relatedSectionsOperationId = findRelatedSectionsOperationId();
    const findRelatedSectionsOperationActivityId = (sectionsOperationActivityId: string) => {
      if (!sectionsOperationActivityId) {
        return null;
      }
      const activeSectionOperationActivities = sectionActivitiesCodes.filter(
        (sectionActivity) => sectionActivity.sectionsOperationId === relatedSectionsOperationId,
      );
      const sectionOperationActivityName = sectionActivitiesCodes.find(
        (sectionOperationActivity) =>
          sectionOperationActivity.sectionsOperationActivityId === sectionsOperationActivityId,
      )?.name;
      const relatedSectionOperationActivityByName = activeSectionOperationActivities.find(
        (sectionOperationActivity) => sectionOperationActivity.name === sectionOperationActivityName,
      );
      if (relatedSectionOperationActivityByName) {
        return relatedSectionOperationActivityByName.sectionsOperationActivityId;
      } else {
        const custumSectionOperationActivityByName = activeSectionOperationActivities.find(
          (sectionOperationActivity) => sectionOperationActivity.name === 'Custom',
        );
        return custumSectionOperationActivityByName?.sectionsOperationActivityId;
      }
    };
    const mappedActivities = copied?.activities.map((activity) => ({
      ...activity,
      sectionsOperationActivityId: findRelatedSectionsOperationActivityId(activity?.sectionsOperationActivityId),
    }));
    return {
      ...copied,
      id: uuidv4(),
      linkedSectionType: section.linkedSectionId,
      sectionType: section.sectionId,
      sectionsOperationId: relatedSectionsOperationId,
      activities: mappedActivities,
    };
  }) as IForecastActivityModel[];
  updatedTemplates[templateIndex].activityModel.push(...mergedOperations);

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: operationsImportRequested.type,
      payload: { templateIndex, mergedOperations, entity },
    }),
    onSuccess: (response) => ({
      type: operationsImported.type,
      payload: response,
    }),
    onError: (error) => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.userSettings_failedToUpdateActivity)}`,
        },
      });
      return {
        type: operationsImportRequestFailed.type,
        payload: error,
      };
    },
  });
};

/**
 * Reorder template
 */
export const reorderTemplate = (
  fromIndex: number,
  toIndex: number,
  templates: ITemplate[],
  entity: string,
  companyId: string,
) => {
  if (fromIndex === toIndex) return;
  const updatedTemplates = cloneDeep(templates);
  const [movedItem] = updatedTemplates.splice(fromIndex, 1);
  updatedTemplates.splice(toIndex, 0, movedItem);

  const url = entity === settingEntity.COMPANY ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: saveTemplatesRequested.type,
    onSuccess: (response) => ({
      type: templateReorder.type,
      payload: { updatedTemplates: response, entity },
    }),
    onError: templatesSaveFailed.type,
  });
};

/**
 * Reorder operation
 */
export const reorderOperationsInSettings = (
  reorder: { fromId: string; toId: string },
  templates: ITemplate[],
  entity: string,
  companyId: string,
) => {
  const result = reorderOperation(reorder, templates);

  if (!result) {
    return;
  }

  const { updatedTemplates, activeTemplateIndex } = result;

  const url = entity === settingEntity.COMPANY ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onSuccess: (response) => {
      return {
        type: operationReorder.type,
        payload: { response, activeTemplateIndex, entity },
      };
    },
    onError: templatesSaveFailed.type,
  });
};

/**
 * Add initial cost item
 */
export const addCostItem = (name: string, templates: ITemplate[], entity: string, parentId = 0, companyId: string) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const costItem = {
    ...initialCostItem,
    costItemId: uuidv4(),
    parentId,
    name,
  } as unknown as ICostItem;
  updatedTemplates[templateIndex].costSetup.costItem.push(costItem);
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: addCostItemRequested.type,
      payload: { templateIndex, costItem, entity },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.costItem_failedToAddCostItem)}`,
        },
      });
    },
  });
};

/**
 * Remove cost item
 */
export const removeCostItem = (id: string, templates: ITemplate[], entity: string, companyId: string) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const costItemIndex = updatedTemplates[templateIndex].costSetup.costItem.findIndex((item) => item.costItemId === id);
  updatedTemplates[templateIndex].costSetup.costItem.splice(costItemIndex, 1);
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';

  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onSuccess: () => ({
      type: costItemRemoved.type,
      payload: { costItemIndex, templateIndex, entity },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.costItem_failedToDeleteCostItem)}`,
        },
      });
      return { type: templatesRequestFailed.type };
    },
  });
};

/**
 *  Update cost item
 */
export const updateCostItem = (
  data: ICostItem,
  templates: ITemplate[],
  selectedId: string,
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const costItemIndex = updatedTemplates[templateIndex].costSetup.costItem.findIndex(
    (item) => item.costItemId === selectedId,
  );
  updatedTemplates[templateIndex].costSetup.costItem[costItemIndex] = data;
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: costItemUpdatedRequested.type,
      payload: { templateIndex, costItemIndex, data, entity },
    }),
    onSuccess: costItemUpdated.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.costItem_failedToUpdateCostItem)}`,
        },
      });
    },
  });
};

/**
 *  Add cost category
 */
export const addCostCategory = (data: ICostCategory, templates: ITemplate[], entity: string, companyId: string) => {
  const { name, parentId, costCodesId } = data;
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const costCategory = {
    costCategoryId: uuidv4(),
    parentId,
    name,
    costCodesId,
  } as unknown as ICostCategory;
  updatedTemplates[templateIndex].costSetup.costCategory.push(costCategory);
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: addCostCategoryRequested.type,
      payload: { templateIndex, costCategory, entity },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.costSetup_failedToAddCostCategory)}`,
        },
      });
    },
  });
};

/**
 *  Update cost category
 */
export const updateCostCategory = (
  data: ICostCategory,
  templates: ITemplate[],
  selectedId: string,
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const costCategoryIndex = updatedTemplates[templateIndex].costSetup.costCategory.findIndex(
    (item) => item.costCategoryId === selectedId,
  );
  const costCategories = updatedTemplates[templateIndex].costSetup.costCategory;
  if (costCategories[costCategoryIndex].costCodesId !== data.costCodesId) {
    updateAllSubCategories(costCategories, data.costCategoryId, data.costCodesId);
  }
  costCategories[costCategoryIndex] = data;
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: costCategoryUpdatedRequested.type,
      payload: { templateIndex, updatedTemplates, entity },
    }),
    onSuccess: costCategoryUpdated.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.costSetup_failedToUpdateCostCategory)}`,
        },
      });
    },
  });
};

/**
 *  Remove cost category
 */
export const removeCostCategory = (
  costCategoryId: string,
  templates: ITemplate[],
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const { costSetup } = updatedTemplates[templateIndex];
  const filteredCostSetup = findConnectedCategories(costSetup, costCategoryId);
  updatedTemplates[templateIndex].costSetup = filteredCostSetup;

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onSuccess: () => ({
      type: costCategoryRemoved.type,
      payload: { templateIndex, filteredCostSetup, entity },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.costSetup_failedToRemoveCostCategory)}`,
        },
      });
      return { type: templatesSaveFailed.type };
    },
  });
};

/**
 * Reorder cost elements
 */
export const reorderElements = (orderedElements: any[], templates: ITemplate[], entity: string, companyId: string) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);
  const orderedElementsData: any[] = orderedElements.map((orderedElement, i) => ({
    ...(orderedElement.itemType === 'category'
      ? { costCategoryId: orderedElement.id }
      : { costItemId: orderedElement.id }),
    parentId: orderedElement.parent || null,
    position: i,
  }));
  const costSetupOrdered = {
    costItem: orderedElementsData.filter((item) => item.costItemId),
    costCategory: orderedElementsData.filter((item) => item.costCategoryId),
  };
  const costSetupUnordered = updatedTemplates[templateIndex].costSetup;
  const data = {
    costItem: [
      ...costSetupUnordered.costItem.map((oldItem) => {
        const newItem = costSetupOrdered.costItem.find((item) => item.costItemId === oldItem.costItemId);
        return newItem ? { ...oldItem, ...newItem } : oldItem;
      }),
      ...costSetupOrdered.costItem.filter(
        (newItem) => !costSetupUnordered.costItem.find((oldItem) => oldItem.costItemId === newItem.costItemId),
      ),
    ],
    costCategory: [
      ...costSetupUnordered.costCategory.map((oldCategory) => {
        const newCategory = costSetupOrdered.costCategory.find(
          (category) => category.costCategoryId === oldCategory.costCategoryId,
        );
        return newCategory ? { ...oldCategory, ...newCategory } : oldCategory;
      }),
      ...costSetupOrdered.costCategory.filter(
        (newCategory) =>
          !costSetupUnordered.costCategory.find(
            (oldCategory) => oldCategory.costCategoryId === newCategory.costCategoryId,
          ),
      ),
    ],
  };

  updatedTemplates[templateIndex].costSetup = data;

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: costSetupReorderRequested.type,
      payload: { data, templateIndex, entity },
    }),
  });
};

/**
 * Duplicate Template
 */
export const duplicateTemplate = (index: number, templates: ITemplate[], entity: string, companyId: string) => {
  const duplicatedTemplate = cloneDeep(templates[index]);
  const newName = nextName(
    templates.map((template) => template.highLevel.name),
    duplicatedTemplate.highLevel.name,
  );

  if (!duplicatedTemplate.costSetup) {
    duplicatedTemplate.costSetup = initialTemplate.costSetup;
  }

  const { costCategory, costItem } = duplicatedTemplate?.costSetup;

  const operationIdsMap = getOperationIdsMap(duplicatedTemplate.activityModel) as { [key: string]: string };
  duplicatedTemplate.activityModel = updateActivityIds(
    duplicatedTemplate.activityModel,
    operationIdsMap,
  ) as IForecastActivityModel[];

  costItem?.forEach((item) => {
    const newItemId = uuidv4();
    item.costItemId = newItemId;
    item.operations = item.operations.map((operationId) => operationIdsMap[operationId] || operationId);
  });

  costCategory?.forEach((category) => {
    const { costCategoryId } = category;
    const newCategoryId = uuidv4();
    category.costCategoryId = newCategoryId;

    updateCostSetupIds(costCategory, costCategoryId, newCategoryId);
    updateCostSetupIds(costItem, costCategoryId, newCategoryId);
  });

  duplicatedTemplate.highLevel.name = newName;
  duplicatedTemplate.templateId = uuidv4();
  const updatedTemplates = [...templates, duplicatedTemplate];
  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: saveTemplatesRequested.type,
    onSuccess: (response) => {
      return {
        type: templatesSaved.type,
        payload: { response, entity },
      };
    },
    onError: templatesSaveFailed.type,
  });
};

/**
 * Upload Template
 */
export const addTemplateFromUpload = (
  templates: ITemplate[],
  template: ITemplate,
  entity: string,
  companyId: string,
) => {
  const updatedTemplates = cloneDeep(templates);
  const newName = nextName(
    updatedTemplates.map((template) => template.highLevel.name),
    template.highLevel.name,
  );
  const newTemplate = { ...template, templateId: uuidv4() };
  newTemplate.highLevel.name = newName;

  updatedTemplates.push(newTemplate);

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: saveTemplatesRequested.type,
    onSuccess: (response) => ({
      type: templatesSaved.type,
      payload: { response, entity },
    }),
    onError: templatesSaveFailed.type,
  });
};

/**
 * Retrieves the section types from the server.
 *
 * @returns {Object} The API call action object.
 */
export const getSectionTypes = () =>
  apiCallBegan({
    url: '/api/operations/linked-sections',
    method: 'GET',
    onSuccess: (response) => ({
      type: sectionTypesFetched.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.userSettings_failedToGetSectionTypes)}`,
        },
      });
    },
  });

/**
 * Add cost item from price book
 */
export const addCostItemsFromPriceBook = (
  templates: ITemplate[],
  priceBookList: IExtendedPriceBook[],
  parentId: string,
  entity: string,
  companyId: string,
  isLinked: boolean,
) => {
  const updatedTemplates = cloneDeep(templates);
  const templateIndex = updatedTemplates.findIndex((item) => item.active);

  const costItems = priceBookList.map((priceBookItem) => {
    let newObject = {
      ...initialCostItem,
      costItemId: uuidv4(),
      name: priceBookItem.name,
      cost: priceBookItem.price,
      currency: priceBookItem.currency,
      vendor: priceBookItem.vendor,
      costCodesId: priceBookItem.costCodesId,
      parentId,
      isLinked,
      priceBookItemId: priceBookItem.priceBookItemId,
    };

    const typeQuantity = typeQuantityMap[priceBookItem.priceType];

    newObject = {
      ...newObject,
      type: typeQuantity.type,
      quantity: typeQuantity.quantity,
    };

    // Set unitCost if the type is 'quantity'
    if (typeQuantity.type === 'quantity') {
      newObject.unitCost = priceBookItem.price;
    }

    return newObject as unknown as ICostItem;
  });
  updatedTemplates[templateIndex].costSetup ??= { costItem: [], costCategory: [] };
  updatedTemplates[templateIndex].costSetup.costItem ??= [];
  updatedTemplates[templateIndex].costSetup.costCategory ??= [];

  updatedTemplates[templateIndex].costSetup.costItem.push(...costItems);

  const url = entity === 'company' ? `/api/templates/${companyId}` : '/api/templates/user';
  return apiCallBegan({
    url,
    method: 'POST',
    data: updatedTemplates,
    onStart: () => ({
      type: addCostItemFromPriceBookRequested.type,
      payload: { updatedTemplates, entity },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: `${i18n.t(translations.costItem_failedToAddCostItem)}`,
        },
      });
    },
  });
};
